import React from "react";
import { AHSeparator, AHText, Box, useTheme } from "arkh";
import { graphql, useFragment } from "react-relay";
import { TodoFooterCircleStatus } from "../../../TodoCategory/TodoFooterCircleStatus";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { TodoResolvedBy } from "./TodoReslovedBy";
import { TodoEditNewMainFooterResponseNumber } from "./TodoEditNewMainFooterResponseNumber";
import { TodoEditNewMainFooterResponseImage } from "./TodoEditNewMainFooterResponseImage";
import { TodoEditNewMainFooter_todo$key } from "./__generated__/TodoEditNewMainFooter_todo.graphql";
import { TodoEditNewMainFooterResponseText } from "./TodoEditNewMainFooterResponseText";
import { FormattedMessage } from "react-intl";

import { useKeyboardState } from "react-native-keyboard-controller";
import { View } from "react-native";
import { ModalTabletAwareKeyboardStickyView } from "../../../../components/mobile/ModalTabletAwareKeyboardStickyView";
import { ResponseWrapper } from "./ResponseWrapper";

export function TodoEditNewMainFooter(props: {
  todo: TodoEditNewMainFooter_todo$key;
  autofocus: boolean;
  keyboardAvoiding: boolean;
}) {
  const todo = useFragment(
    graphql`
      fragment TodoEditNewMainFooter_todo on Todo {
        id
        ...TodoFooterCircleStatus_todo
        lastChangedStatusBy {
          name
        }
        canChangeStatus
        ...TodoEditNewMainFooterResponseImage_todo
        ...TodoEditNewMainFooterResponseNumber_todo
        ...TodoEditNewMainFooterResponseText_todo
        response {
          ... on TodoResponseNumber {
            __typename
          }
          ... on TodoResponseImage {
            __typename
          }
          ... on TodoResponseText {
            __typename
          }
        }
        status
      }
    `,
    props.todo
  );
  const insets = useSafeAreaInsets();

  return (
    <InnerKeyboardAvoiding avoid={props.keyboardAvoiding}>
      <Box
        color="surface-primary"
        borderColor="border-primary"
        style={{
          borderTopWidth: 1,
          marginBottom: props.keyboardAvoiding ? -insets.bottom : 0,
        }}
        insets={{
          bottom: insets.bottom,
        }}
      >
        {todo.response?.__typename === "TodoResponseImage" ? (
          <ResponseWrapper>
            <TodoEditNewMainFooterResponseImage todo={todo} />
          </ResponseWrapper>
        ) : null}
        {todo.response?.__typename === "TodoResponseNumber" ? (
          <ResponseWrapper>
            <TodoEditNewMainFooterResponseNumber
              todo={todo}
              autofocus={props.autofocus}
            />
          </ResponseWrapper>
        ) : null}
        {todo.response?.__typename === "TodoResponseText" ? (
          <ResponseWrapper>
            <TodoEditNewMainFooterResponseText
              todo={todo}
              autoFocus={props.autofocus}
            />
          </ResponseWrapper>
        ) : null}
        <AHSeparator />
        <Box
          style={{ flexDirection: "row", alignItems: "center" }}
          space="medium"
        >
          <TodoFooterCircleStatus todo={todo} size="medium" />
          <Box spaceLeft="small">
            {todo.status === "DONE" ? (
              <TodoResolvedBy
                name={todo.lastChangedStatusBy?.name ?? ""}
                type="body"
              />
            ) : (
              <AHText color="text-primary">
                {todo.canChangeStatus && todo.response ? (
                  <FormattedMessage
                    defaultMessage="Check to submit"
                    description="TodoEditMainFooter check to submit cta"
                  />
                ) : (
                  ""
                )}
              </AHText>
            )}
          </Box>
        </Box>
      </Box>
    </InnerKeyboardAvoiding>
  );
}

function InnerKeyboardAvoiding(props: {
  avoid: boolean;
  children: React.ReactNode;
}) {
  const insets = useSafeAreaInsets();
  const theme = useTheme();
  const isKeyboardVisible = useKeyboardState((s) => s.type);

  if (props.avoid) {
    return (
      <ModalTabletAwareKeyboardStickyView
        offset={{
          closed: -insets.bottom,
        }}
      >
        {props.children}
      </ModalTabletAwareKeyboardStickyView>
    );
  }
  return (
    <View
      style={{
        backgroundColor: theme["surface"],
        paddingBottom: isKeyboardVisible ? 0 : insets.bottom,
      }}
    >
      {props.children}
    </View>
  );
}
